// src/components/common/AuthGuard.tsx

import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, ApiError } from '../../services/api.client';
import type { User } from '../../types/auth';

// Solo per account admin/superadmin con password temporanea (vedi admin:create-user
// lato backend) — mai impostato dai flussi self-service degli utenti normali.
const FORCE_PASSWORD_CHANGE_PATH = '/cambio-password-obbligatorio';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lastCheckedPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastCheckedPath.current === pathname) return;
    lastCheckedPath.current = pathname;

    const verify = async () => {
      try {
        const freshUser = await apiClient.get<User>('/users/me');
        updateUser(freshUser);

        if (
          freshUser.mustChangePassword &&
          pathname !== FORCE_PASSWORD_CHANGE_PATH
        ) {
          navigate(FORCE_PASSWORD_CHANGE_PATH, { replace: true });
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await logout();
          navigate('/login', { replace: true });
        }
        // Errori di rete o 500 non forzano il logout
      }
    };

    verify();
  }, [pathname, logout, navigate, updateUser]);

  return <>{children}</>;
};

export default AuthGuard;
